import { Injectable, signal } from '@angular/core';
import { AuthUser, LoginResponse } from './models';

const TOKEN_KEY = 'panakyc.frontend.token';
const USER_KEY = 'panakyc.frontend.user';

type JwtPayload = {
  exp?: number;
};

function getStorage(): Storage | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  return localStorage;
}

function readStoredToken(): string | null {
  const storage = getStorage();
  return storage?.getItem(TOKEN_KEY) ?? null;
}

function decodeBase64Url(value: string): string | null {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return atob(padded);
  } catch {
    return null;
  }
}

function parseJwtPayload(token: string): JwtPayload | null {
  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }

  const payloadText = decodeBase64Url(segments[1]);
  if (!payloadText) {
    return null;
  }

  try {
    const payload = JSON.parse(payloadText) as unknown;
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    return payload as JwtPayload;
  } catch {
    return null;
  }
}

function isJwtTokenValid(token: string | null, now = Date.now()): boolean {
  if (!token) {
    return false;
  }

  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
    return false;
  }

  return payload.exp * 1000 > now;
}

function readStoredUser(): AuthUser | null {
  const storage = getStorage();
  const raw = storage?.getItem(USER_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthStoreService {
  private readonly tokenState = signal<string | null>(readStoredToken());
  private readonly userState = signal<AuthUser | null>(readStoredUser());

  constructor() {
    const token = this.tokenState();
    if (!isJwtTokenValid(token) && (token !== null || this.userState() !== null)) {
      this.clearPersistedSession();
    }
  }

  private clearPersistedSession() {
    this.tokenState.set(null);
    this.userState.set(null);

    const storage = getStorage();
    storage?.removeItem(TOKEN_KEY);
    storage?.removeItem(USER_KEY);
  }

  private validatedToken(): string | null {
    const token = this.tokenState();
    return isJwtTokenValid(token) ? token : null;
  }

  readonly token = () => this.validatedToken();
  readonly currentUser = () => {
    return this.validatedToken() ? this.userState() : null;
  };
  readonly isAuthenticated = () => this.validatedToken() !== null;
  readonly isPlatformUser = () => {
    const role = this.currentUser()?.role;
    return role === 'platform_administrator' || role === 'admin';
  };
  readonly canAccessOrganizationApp = () => {
    return this.isAuthenticated() && !this.isPlatformUser() && this.currentUser() !== null;
  };
  readonly canManageQuestionnaires = () => {
    const role = this.currentUser()?.role;
    return role === 'organization_administrator';
  };
  readonly canManageUsers = () => {
    return this.currentUser()?.role === 'organization_administrator';
  };
  readonly canManageMatrices = () => {
    return this.currentUser()?.role === 'organization_administrator';
  };
  readonly canManageBusinesses = () => {
    return this.currentUser()?.role === 'organization_administrator';
  };
  readonly canManageInvitations = () => {
    return this.currentUser()?.role === 'organization_administrator';
  };

  setSession(session: LoginResponse) {
    if (!isJwtTokenValid(session.token)) {
      this.clearPersistedSession();
      return;
    }

    this.tokenState.set(session.token);
    this.userState.set(session.user);

    const storage = getStorage();
    storage?.setItem(TOKEN_KEY, session.token);
    storage?.setItem(USER_KEY, JSON.stringify(session.user));
  }

  clearSession() {
    this.clearPersistedSession();
  }
}
